/**
 * API client with configurable base URL.
 * Uses VITE_API_URL env var, defaults to http://localhost:8000.
 *
 * Auth: when a Sign in with Apple session token exists it is attached as a
 * Bearer header; a 401 means the session is gone (revoked/expired), so we
 * drop it and land on the login screen.
 */

import { authHeaders, redirectToLogin } from './auth'
import { noteAnswered, startBusyChain } from './busy'
import { BusyBackoff, busyRetryAfter, isRetryableMethod, sleepAbortable } from './busyRetry'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

export interface ApiError {
  status: number
  message: string
  url: string
  /**
   * True on a 503 the server marked busy (`Retry-After`, #391) that is out of
   * retries, or that was a write and so was never sent again. It is the only 503
   * that means "ask again in a moment": the health probe's 503 and sign-in's
   * "not configured" carry no such header. `busyRetry.ts` has the rule.
   */
  busy?: true
  /** What the server asked us to wait, in seconds; set with `busy`. */
  retryAfterS?: number
}

/**
 * The seconds a response asks us to wait if it is a busy 503, else null. Only a
 * 503 is asked for its headers, and a stand-in without any (a test's hand-built
 * response) is treated as having none, so it is never "busy" and never throws.
 */
function busyAnswer(res: Response): number | null {
  if (res.status !== 503) return null
  return busyRetryAfter(res.status, res.headers?.get('Retry-After') ?? null)
}

/**
 * `fetch`, sending a read again when the server answers "busy" (#392).
 *
 * A read that gets a 503 with `Retry-After` waits what it asks, with backoff and
 * jitter, inside a budget (`BusyBackoff`), and goes again; the caller sees only
 * the final answer. Every other request is sent once: a write is never sent
 * again, because the 503 can arrive after part of its work was done (the head of
 * `busyRetry.ts`). When the budget is spent the last 503 is returned, as `fetch`
 * would have returned it. An abort ends the wait and rejects like `fetch` does.
 *
 * `label` names the request for `busy.ts`, which a "busy" state reads.
 */
async function fetchRetryingBusy(url: string, init: RequestInit, label: string): Promise<Response> {
  const retryable = isRetryableMethod(init.method)
  const chain = retryable ? startBusyChain(label) : null
  const backoff = new BusyBackoff()
  try {
    for (;;) {
      const res = await fetch(url, init)
      const retryAfterS = busyAnswer(res)
      if (retryAfterS === null) {
        // Any answer but a busy 503 says the server is up. A write has no chain,
        // and its answer breaks the streak all the same.
        if (chain) chain.answered()
        else noteAnswered()
        return res
      }
      // A write refused as busy is not sent again and is not counted: the line
      // it earns is its own (`writeErrorLine`), not the loading surfaces'.
      if (chain === null) return res
      chain.answeredBusy()
      const delay = backoff.next(retryAfterS)
      if (delay === null) return res
      // The refused answer is not read: let its connection go.
      res.body?.cancel().catch(() => {})
      await sleepAbortable(delay, init.signal)
    }
  } finally {
    chain?.end()
  }
}

/**
 * Typed fetch wrapper with base URL resolution and error normalization.
 *
 * A read that is refused as busy is sent again before it fails
 * (`fetchRetryingBusy`); every other failure is thrown at once, as before.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${BASE_URL}${path}`
  const res = await fetchRetryingBusy(
    url,
    {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...init?.headers,
      },
    },
    path,
  )

  if (!res.ok) {
    if (res.status === 401) redirectToLogin()
    const body = await res.text().catch(() => '')
    const error: ApiError = {
      status: res.status,
      message: body || res.statusText,
      url,
    }
    const retryAfterS = busyAnswer(res)
    if (retryAfterS !== null) {
      error.busy = true
      error.retryAfterS = retryAfterS
    }
    throw error
  }

  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

/**
 * Auth-aware fetch that returns the raw Response — for SSE streaming and other
 * callers that read the body themselves (e.g. the coach chat). Same 401
 * handling as apiFetch, no base-URL prefix (callers pass proxied paths), and the
 * same busy retry for a read. A POST (the chat) is sent once.
 */
export async function rawApiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetchRetryingBusy(
    path,
    {
      ...init,
      headers: { ...authHeaders(), ...init?.headers },
    },
    path,
  )
  if (res.status === 401) redirectToLogin()
  return res
}

/**
 * Multipart upload. Deliberately NOT `apiFetch`.
 *
 * `apiFetch` sets `Content-Type: application/json` on every request, and a
 * multipart body needs the BROWSER to set that header so it can include its
 * own generated boundary. Overriding it leaves the server unable to parse the
 * parts, which surfaces as a 422 naming a field the client did send.
 */
export async function uploadApiFetch(path: string, file: File): Promise<void> {
  const body = new FormData()
  body.append('file', file)
  const res = await rawApiFetch(`${BASE_URL}${path}`, { method: 'POST', body })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    const error: ApiError = { status: res.status, message: detail || res.statusText, url: path }
    throw error
  }
}

export { BASE_URL }
